/**
 * Engagement Lookups Hook
 * React hook for loading and accessing engagement lookup data
 * Used for domain entities like Organizations, UserProfiles, etc.
 */

import { useState, useEffect } from 'react'
import { useEngagementApi } from '@/hooks/use-engagement-api'
import type { LookupValue, LookupCategory } from '@/types/api/engagement'

interface UseEngagementLookupsReturn {
  // Loading state
  loading: boolean
  error: string | null
  loaded: boolean
  
  // Data
  categories: string[]
  
  // Helper functions
  getByCategory: (categoryName: string) => LookupValue[]
  getByCategoryUnsorted: (categoryName: string) => LookupValue[]
  getValue: (categoryName: string, code: string) => LookupValue | undefined
  getDescription: (categoryName: string, code: string) => string
  
  // Actions
  refresh: () => Promise<void>
}

// Global cache for lookups (shared across all hook instances)
let lookupsCache: Map<string, LookupValue[]> = new Map()
let allCategoriesCache: LookupCategory[] = []
let isLoaded = false

export function useEngagementLookups(): UseEngagementLookupsReturn {
  const engagementApi = useEngagementApi()
  const [loading, setLoading] = useState(!isLoaded)
  const [error, setError] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>(
    allCategoriesCache.map(cat => cat.categoryName || '').filter(name => name)
  )

  const loadEngagementLookups = async () => {
    // Don't reload if already loaded
    if (isLoaded) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      console.log('Loading engagement lookups...')
      const allLookups = await engagementApi.getAllLookups()
      
      // Cache the results
      allCategoriesCache = allLookups
      lookupsCache.clear()
      allLookups.forEach(category => {
        if (category.categoryName && category.values) {
          lookupsCache.set(category.categoryName, category.values)
        }
      })
      
      setCategories(allLookups.map(cat => cat.categoryName || '').filter(name => name))
      isLoaded = true
      
      console.log(`Engagement lookups loaded successfully: ${allLookups.length} categories`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load engagement lookups'
      setError(errorMessage)
      console.error('Failed to load engagement lookups:', err)
    } finally {
      setLoading(false)
    }
  }

  const getByCategory = (categoryName: string): LookupValue[] => {
    const values = lookupsCache.get(categoryName) || []
    return values.sort((a, b) => (a.value || '').localeCompare(b.value || ''))
  }

  const getByCategoryUnsorted = (categoryName: string): LookupValue[] => {
    return lookupsCache.get(categoryName) || []
  }

  const getValue = (categoryName: string, code: string): LookupValue | undefined => {
    const values = getByCategory(categoryName)
    return values.find(v => v.code === code)
  }

  const getDescription = (categoryName: string, code: string): string => {
    const lookupValue = getValue(categoryName, code)
    return lookupValue?.description || lookupValue?.value || code
  }

  const refresh = async () => {
    // Force reload by clearing cache first
    lookupsCache.clear()
    allCategoriesCache = []
    isLoaded = false
    setCategories([])
    await loadEngagementLookups()
  }

  // Load engagement lookups on mount
  useEffect(() => {
    loadEngagementLookups()
  }, [])

  return {
    // Loading state
    loading,
    error,
    loaded: isLoaded,
    
    // Data
    categories,
    
    // Helper functions
    getByCategory,
    getByCategoryUnsorted,
    getValue,
    getDescription,
    
    // Actions
    refresh
  }
} 